/**
 * API fetch wrapper.
 *
 * - 自動 attach `Authorization: Bearer <jwt>`
 * - 401 → clearAccessToken() → 廣播 logout 事件
 * - !ok → throw ApiError（保留 status + RFC 7807 problem body）
 * - credentials: 'include'（為將來 refresh_token httpOnly cookie 預留）
 *
 * dev: VITE_API_BASE=http://localhost:3000/api/v1（對應 b-cors 之 ALLOWED_ORIGINS）
 */

import { clearAccessToken, getAccessToken } from './auth';
import { getStoredLocale } from './i18n';
// 註：`./api-schema` 目前由 openapi-typescript 從 docs/api/openapi.yaml 自動生成 paths/operations 索引，
// 但後端尚未對每個 @Post / @Get 加 @ApiResponse({ type }) decorator，故 response content 多為 `never`。
// 待後端補完 type-bearing decorators 後可換成 `paths['...']['post']['responses'][201]['content']['application/json']` 形式。
// 現階段為求可編譯，先用 inline 最小子集 type。
export interface LoginRequest {
  email: string;
  password: string;
}
export interface LoginResponse {
  session_id: string;
  two_factor_required: boolean;
  access_token?: string; // 序 1C：未需 2FA 時直接帶；2FA flow 走 /auth/login/totp 後取
}
export interface AuthUser {
  user_id: string;
  email: string;
  display_name?: string;
  permissions: string[];
  requires_2fa: boolean;
}
export interface ProductSummary {
  id: string;
  sku: string;
  category: string;
  name_i18n: Record<string, string>;
  description_i18n: Record<string, string> | null;
  /** numeric string from PostgreSQL numeric column */
  base_price: string;
  currency: string;
  duration_minutes: number | null;
  channels: string[];
  is_published: boolean;
}
export interface ProductListResponse {
  items: ProductSummary[];
  total: number;
  limit: number;
  offset: number;
}
export interface ScheduleSummary {
  id: string;
  product_id: string;
  /** ISO 8601 timestamp（含時區） */
  departure_at: string;
  departure_end_at: string;
  capacity: number;
  reserved_count: number;
  weather_cancelled: boolean;
}
export interface ScheduleListResponse {
  items: ScheduleSummary[];
}
export interface CreateOrderItem {
  product_id: string;
  schedule_id: string;
  quantity: number;
  /** numeric string — 與後端 numeric column 對齊；前端組單時可帶 number，JSON.stringify 會序列化為數字字面值 */
  unit_price: number | string;
  /** 票種；後端依此解析對應 pricing 規則。預設 adult。 */
  ticket_type?: string;
  /**
   * 這筆是哪張套票的一段；單買時省略。
   *
   * 套票不建班次——顧客為每一段各挑一個時段，同一張套票的每一段都帶同一個值。
   */
  package_product_id?: string;
}
export interface CreateOrderRequest {
  channel: 'web' | 'kiosk' | 'partner';
  subtotal: number | string;
  total: number | string;
  items: CreateOrderItem[];
  buyer_email?: string;
  buyer_phone?: string;
  buyer_name?: string;
}
export type OrderState =
  | 'HOLD'
  | 'PAYMENT_PENDING'
  | 'PAID'
  | 'EXPIRED'
  | 'CANCELLED'
  | 'PAYMENT_FAILED'
  | 'OVERSOLD';
export interface OrderSummary {
  id: string;
  state: OrderState;
  /** numeric string from PostgreSQL */
  subtotal: string;
  total: string;
  /** ISO timestamp；HOLD 期間有，PAID/EXPIRED 之後可能保留為歷史欄位 */
  hold_expires_at?: string;
}
/**
 * GET /orders/:id response — 訂單詳情（含即時 state；對應後端 OrderResponseDto）。
 * 與 `OrderSummary` 之差別：多 order_number / channel / created_at。
 */
export interface OrderDetail {
  id: string;
  /** 人類可讀訂單編號（ORD-YYYYMMDD-XXXXXXXX） */
  order_number: string;
  channel: string;
  state: OrderState;
  subtotal: string;
  total: string;
  /** ISO timestamp */
  created_at: string;
  /** HOLD 階段佔位 TTL；非 HOLD 終態為 null */
  hold_expires_at: string | null;
}
export interface PaymentCallbackResponse {
  outcome: 'paid' | 'oversold' | 'failed';
  order: OrderSummary;
  /** 僅 outcome='paid' 時帶；PAID + 同 tx 發票 */
  issuedTicketIds?: string[];
}
export type TicketState = 'valid' | 'used' | 'void_pending_refund' | 'voided' | 'expired';
/**
 * GET /tickets/:id/qr response — ownership-aware QR fetch。
 * 對應後端 TicketQrResponseDto；audit_log 寫 'ticket.qr_view'。
 */
export interface TicketQr {
  ticket_id: string;
  ticket_code: string;
  ticket_state: TicketState;
  refund_risk_class: 'REFUND_ELIGIBLE' | 'REFUND_LOCKED';
  /** compact JWS（三段式 `header.payload.signature`）— 直接給 QR encoder */
  qr_payload: string;
}

/** 退款狀態機 12 態（同步 packages/domain/src/refund/refund-state.ts）。 */
export type RefundState =
  | 'requested'
  | 'refund_authorized'
  | 'refund_settling'
  | 'refund_settled'
  | 'allowance_pending'
  | 'allowance_issued'
  | 'completed'
  | 'refund_failed'
  | 'allowance_failed'
  | 'manual_review'
  | 'cancelled'
  | 'rejected';

/**
 * GET /refunds/:id response — AC-E2-7 雙欄；AC-DM-1 對外禁單一 status。
 */
export interface RefundRecord {
  id: string;
  order_id: string;
  ticket_id: string | null;
  refund_state: RefundState;
  ticket_state: TicketState | null;
  refund_settled_but_allowance_pending: boolean;
  manual_review: boolean;
  refund_amount: string;
  created_at: string;
}

export interface CreateRefundRequest {
  ticket_id: string;
  /** 1..500 字元 */
  reason: string;
  /** 不帶則 BE 以 order_item.line_total fallback */
  refund_amount?: number;
}

export interface CreateRefundResponse {
  id: string;
  refund_request_id: string;
  refund_state: RefundState;
  refund_amount: string;
  ticket_id: string;
  order_id: string;
}
/**
 * GET /tickets/:id response — display 用（含多語文案 + 雙欄 state）。
 * 不含 qr_payload（M1 後端 by design：避免 buyer API 洩 QR；走 /qr 端點）。
 */
export interface TicketDisplay {
  ticket_id: string;
  ticket_state: TicketState;
  refund_state: string | null;
  refund_settled_but_allowance_pending: boolean;
  manual_review: boolean;
  display: { message: string; icon: string; can_board: boolean; prd_ref: string };
  meta: { fallback: boolean; locale: string };
}
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  [key: string]: unknown;
}

// ---------- C-1 智慧行程推薦（AC-C1-1/2/3）----------
export interface RecommendRequest {
  stay_minutes: number;
  budget?: number | null;
  party_adults: number;
  party_children?: number;
  interests?: string[];
}
export interface RecommendProduct {
  product_id: string;
  sku: string;
  name_i18n: Record<string, string>;
  category: string;
  duration_minutes: number;
  base_price: string;
}
export interface RecommendItinerary {
  id: string;
  products: RecommendProduct[];
  estimated_minutes: number;
  total_price: string;
  fit_note: string;
}
export interface RecommendResponse {
  party_size: number;
  stay_minutes: number;
  budget: number | null;
  recommendations: RecommendItinerary[];
}

export const API_BASE: string = import.meta.env.VITE_API_BASE ?? 'http://localhost:3000/api/v1';

export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly status: number;
  readonly problem?: ProblemDetails;

  constructor(status: number, message: string, problem?: ProblemDetails) {
    super(message);
    this.status = status;
    if (problem !== undefined) {
      this.problem = problem;
    }
  }
}

export interface ApiFetchInit extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** 跳過 Bearer attach（如 /auth/login） */
  skipAuth?: boolean;
}

async function parseProblem(res: Response): Promise<ProblemDetails | undefined> {
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    return undefined;
  }
  try {
    return (await res.json()) as ProblemDetails;
  } catch {
    return undefined;
  }
}

export async function apiFetch<T = unknown>(path: string, init: ApiFetchInit = {}): Promise<T> {
  const { body, skipAuth, headers: initHeaders, ...rest } = init;
  const headers = new Headers(initHeaders);

  if (body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  // 後端依 Accept-Language 在地化部分 response（如 TicketDisplay.display.message）
  if (!headers.has('Accept-Language')) {
    headers.set('Accept-Language', getStoredLocale());
  }

  if (!skipAuth) {
    const token = getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  const requestInit: RequestInit = {
    ...rest,
    headers,
    credentials: 'include',
  };
  if (body !== undefined) {
    requestInit.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  const res = await fetch(`${API_BASE}${path}`, requestInit);

  if (res.status === 401) {
    clearAccessToken();
    const problem = await parseProblem(res);
    throw new ApiError(401, problem?.detail ?? 'Unauthorized', problem);
  }

  if (!res.ok) {
    const problem = await parseProblem(res);
    const msg = problem?.detail ?? problem?.title ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, problem);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

// ---------- 端點封裝 ----------

export function login(payload: LoginRequest): Promise<LoginResponse> {
  return apiFetch<LoginResponse>('/auth/login', {
    method: 'POST',
    body: payload,
    skipAuth: true,
  });
}

export function logout(): Promise<void> {
  return apiFetch<void>('/auth/logout', { method: 'POST' });
}

export function getMe(): Promise<AuthUser> {
  return apiFetch<AuthUser>('/auth/me', { method: 'GET' });
}

/**
 * 可售商品清單。
 *
 * 過濾在後端做。此前這支連參數都沒帶，於是**未上架商品**與**未開放 web 通路的
 * 商品**都會出現在會員站台上——CMS 的「上下架」與「銷售通路」設定形同虛設。
 */
export function listProducts(): Promise<ProductListResponse> {
  const qs = new URLSearchParams({ channel: 'web', is_published: 'true' });
  return apiFetch<ProductListResponse>(`/products?${qs.toString()}`, { method: 'GET' });
}

export function getProduct(id: string): Promise<ProductSummary> {
  return apiFetch<ProductSummary>(`/products/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

/**
 * POST /recommendations — C-1 智慧行程推薦。
 *
 * 公開 endpoint（不需登入）；現場遊客輸入條件即取得行程組合建議。
 */
export function getRecommendations(payload: RecommendRequest): Promise<RecommendResponse> {
  return apiFetch<RecommendResponse>('/recommendations', {
    method: 'POST',
    body: payload,
    skipAuth: true,
  });
}

export function listSchedules(productId: string): Promise<ScheduleListResponse> {
  const qs = new URLSearchParams({ product_id: productId });
  return apiFetch<ScheduleListResponse>(`/schedules?${qs.toString()}`, {
    method: 'GET',
  });
}

/** 套票的一段（GET /products/:id/components）。公開端點。 */
export interface PackageComponent {
  component_product_id: string;
  sort_order: number;
  sku: string;
  name_i18n: Record<string, string>;
  description_i18n: Record<string, string> | null;
  duration_minutes: number | null;
  base_price: string;
  cover_media_id: string | null;
}

/**
 * 套票含哪些商品（依呈現順序）。回空陣列 = 尚未設定組成，該套票不可售。
 */
export function listProductComponents(productId: string): Promise<PackageComponent[]> {
  return apiFetch<PackageComponent[]>(`/products/${encodeURIComponent(productId)}/components`, {
    method: 'GET',
  });
}

/** 可售票種 + 各票種基本單價（GET /pricing/ticket-types）。公開端點。 */
export interface SellableTicketType {
  ticket_type: string;
  display_name_i18n: Record<string, string> | null;
  unit_price: string;
  /** 折扣前原價（劃線價）；> unit_price 表示有促銷。 */
  original_price: string;
  pricing_id: string | null;
  rule_summary: string | null;
}
/**
 * 省略 `schedule_id` = 查套票票種：套票不建班次，後端此時只回商品價目表、
 * 不套 pricing 規則（規則掛在 (product, schedule) 上，對套票沒有可匹配對象）。
 */
export function getTicketTypes(params: {
  product_id: string;
  schedule_id?: string;
  channel: string;
}): Promise<{ ticket_types: SellableTicketType[] }> {
  const qs = new URLSearchParams({
    product_id: params.product_id,
    channel: params.channel,
    ...(params.schedule_id === undefined ? {} : { schedule_id: params.schedule_id }),
  });
  return apiFetch<{ ticket_types: SellableTicketType[] }>(
    `/pricing/ticket-types?${qs.toString()}`,
    { method: 'GET', skipAuth: true },
  );
}

/** 分票種報價（POST /pricing/quote）。公開端點；顯示折後價，與下單同源。 */
export interface PricingQuoteLine {
  ticket_type: string;
  quantity: number;
  unit_price: string;
  /** 折扣前原價（劃線價）；> unit_price 表示有促銷。 */
  original_price: string;
  line_total: string;
  pricing_id: string | null;
  rule_summary: string | null;
}
export interface PricingQuoteResponse {
  lines: PricingQuoteLine[];
  party_size: number;
  total: string;
}
export function getPricingQuote(payload: {
  product_id: string;
  schedule_id: string;
  channel: string;
  tickets: Array<{ ticket_type: string; quantity: number }>;
}): Promise<PricingQuoteResponse> {
  return apiFetch<PricingQuoteResponse>('/pricing/quote', {
    method: 'POST',
    body: payload,
    skipAuth: true,
  });
}

export function createOrder(
  payload: CreateOrderRequest,
  idempotencyKey: string,
): Promise<OrderSummary> {
  return apiFetch<OrderSummary>('/orders', {
    method: 'POST',
    body: payload,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

/** GET /orders/:id — 訂單詳情（即時 state；buyer 由 localStorage 之 id 取用）。 */
export function getOrder(id: string): Promise<OrderDetail> {
  return apiFetch<OrderDetail>(`/orders/${encodeURIComponent(id)}`, { method: 'GET' });
}

export function initiatePayment(orderId: string): Promise<{ order: OrderSummary }> {
  return apiFetch<{ order: OrderSummary }>(`/orders/${encodeURIComponent(orderId)}/events`, {
    method: 'POST',
    body: { event: 'initiate_payment' },
  });
}

export function paymentCallback(
  orderId: string,
  providerTransactionId: string,
): Promise<PaymentCallbackResponse> {
  return apiFetch<PaymentCallbackResponse>(
    `/orders/${encodeURIComponent(orderId)}/payment-callback`,
    {
      method: 'POST',
      body: { status: 'success', provider_transaction_id: providerTransactionId },
    },
  );
}

/**
 * Ownership-aware QR fetch — 需 auth + tickets.read 權限；audit 寫 ticket.qr_view。
 * 取代既有手寫 getTicket（後端 GET /tickets/:id 是 display DTO，無 qr_payload）。
 */
export function getTicketQr(id: string): Promise<TicketQr> {
  return apiFetch<TicketQr>(`/tickets/${encodeURIComponent(id)}/qr`, {
    method: 'GET',
  });
}

export function getTicket(id: string): Promise<TicketQr> {
  return apiFetch<TicketQr>(`/tickets/${encodeURIComponent(id)}/qr`, {
    method: 'GET',
  });
}

/**
 * GET /tickets/:id — 票券 display DTO（多語文案 + can_board；不含 qr_payload）。
 * 後端依 `Accept-Language`（apiFetch 自動帶）回在地化 `display.message`。
 */
export function getTicketDisplay(id: string): Promise<TicketDisplay> {
  return apiFetch<TicketDisplay>(`/tickets/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

/**
 * POST /refunds — buyer-initiated 退票申請（AC-B2-4 冪等鍵）。
 * 同一 idempotencyKey 重送回同 refund_request_id。
 */
export function createRefund(
  payload: CreateRefundRequest,
  idempotencyKey: string,
): Promise<CreateRefundResponse> {
  return apiFetch<CreateRefundResponse>('/refunds', {
    method: 'POST',
    body: payload,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

/** GET /refunds/:id — AC-E2-7 雙欄 (refund_state + ticket_state)。 */
export function getRefund(id: string): Promise<RefundRecord> {
  return apiFetch<RefundRecord>(`/refunds/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}
